<script setup>
import {onMounted, ref, computed, defineProps, watch} from 'vue';
import { getDrivers } from "@/services/userService";
import {formatNum} from "leaflet/src/core/Util";

// Props
const props = defineProps({
  show: {
    type: Boolean,
    required: true,
  },
  onConfirm: {
    type: Function,
    required: true,
  },
  onCancel: {
    type: Function,
  },
  trip: {
    type: Object,
  }
});

// Drivers list
const drivers = ref([]);
const searchQuery = ref('');
const selectedDriver = ref(null);

// Obtener la lista de conductores al montar el componente
const fetchDrivers = async () => {
  try {
    drivers.value = await getDrivers();
    // Asegúrate que drivers.value ahora es un array de objetos con {name, licensePlate, id }
    // Ejemplo: [{id: 1, name: "Juan", licensePlate: "XYZ123"}, ...]
  } catch (err) {
    console.error(err);
  }
};

// Filtrar conductores según la consulta de búsqueda
const filteredDrivers = computed(() => {
  const query = searchQuery.value.toLowerCase();
  return drivers.value.filter(driver => {
    const driverName = (driver.name || '').toString().toLowerCase();
    const driverPlate = (driver.licensePlate || '').toString().toLowerCase();
    return driverName.includes(query) || driverPlate.includes(query);
  });
});

// Al montar el componente
onMounted(() => {
  fetchDrivers();
});

// Observar el cambio de la propiedad show
watch(() => props.show, (newVal) => {
  if (newVal) {
    fetchDrivers();
    selectedDriver.value = {};
    searchQuery.value = '';
  }
});

</script>

<template>
  <!-- Overlay -->
  <transition name="fade">
    <div
        v-if="props.show"
        class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50"
        aria-labelledby="confirmModalTitle"
        aria-describedby="confirmModalMessage"
        role="dialog"
        aria-modal="true"
    >
      <!-- Modal Container -->
      <div class="bg-white dark:bg-gray-900 rounded-lg shadow-lg max-w-md w-full mx-4 p-6">
        <!-- Header -->
        <h2
            id="confirmModalTitle"
            class="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-4"
        >
          Assign Driver
        </h2>

        <!-- Body (Message) -->
        <div
            id="confirmModalMessage"
            class="text-gray-700 dark:text-gray-300 text-center text-lg mb-6"
        >
          <div class="my-3">Select a driver to assign to the trip</div>
          <div class="mb-4">
            <input
                type="text"
                v-model="searchQuery"
                placeholder="Search driver by name"
                class="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring focus:ring-blue-500 text-black dark:text-white dark:bg-gray-800 bg-white"
            />
          </div>
          <div class="drivers-container">
            <ul class="space-y-2">
              <li
                  v-for="driver in filteredDrivers"
                  :key="driver.id"
                  class="flex justify-between items-center p-2 border rounded-md cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700"
                  @click="selectedDriver = driver"
                  :class="{'bg-gray-300 dark:bg-gray-800': selectedDriver === driver}"
              >
                <span>{{ driver.name }} (Rating: {{ formatNum(driver.rating, 1)}})</span>
                <span v-if="selectedDriver === driver" class="text-blue-500 font-bold">Selected</span>
              </li>
            </ul>
          </div>
        </div>

        <!-- Footer (Actions) -->
        <div class="flex justify-end space-x-3">
          <button
              class="px-4 py-2 rounded-md bg-gray-300 hover:bg-gray-400 text-gray-800 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-white transition-all duration-200"
              @click="props.onCancel"
          >
            Cancel
          </button>
          <button
              class="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white dark:bg-blue-500 dark:hover:bg-blue-600 transition-all duration-200"
              @click="() => props.onConfirm(selectedDriver)"
              v-if="selectedDriver"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<style scoped>
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}
.drivers-container{
  max-height: 200px;
  overflow-y: auto;
}
</style>
